import { useRef, useEffect } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '@videojs/themes/dist/city/index.css';
import '@videojs/http-streaming';
import 'videojs-youtube';
import '../../index.css'; // Ensure you have the correct path to your CSS file

const VideoPlayer = ({ options, onReady }) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const observerRef = useRef(null);

    useEffect(() => {
        videojs.log.level('error');
        // inject a small stylesheet once to make posters/iframes cover the player area
        if (!document.getElementById('videojs-cover-poster-style')) {
            const style = document.createElement('style');
            style.id = 'videojs-cover-poster-style';
            style.innerHTML = `
        /* Make the poster use cover so thumbnails fully cover the player */
        .vjs-poster { background-size: cover !important; background-position: center center !important; background-repeat: no-repeat !important; }
        /* Ensure the tech (video/iframe) fills the player and uses object-fit: cover */
        .video-js .vjs-tech, .video-js iframe, .video-js video, .vjs-poster img { object-fit: cover !important; width: 100% !important; height: 100% !important; }
        /* When we hide the iframe on pause we show the poster instead to avoid YouTube endscreen/suggestions */
        .vjs-hide-iframe-on-pause .vjs-tech { visibility: hidden !important; }
        .vjs-hide-iframe-on-pause .vjs-poster { display: block !important; opacity: 1 !important; }
  /* Play icon overlay centered on poster */
        .vjs-play-overlay { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); z-index: 20; display: flex; align-items: center; justify-content: center; width: 84px; height: 84px; background: rgba(0,0,0,0.6); border-radius: 50%; cursor: pointer; }
        .vjs-play-overlay svg { width: 36px; height: 36px; fill: white; }
        /* Hide overlay while playing */
        .vjs-playing .vjs-play-overlay { display: none !important; }
  /* Hide any info controls (multiple variants and attributes) */
  .video-js .vjs-info-button, .video-js .vjs-control.vjs-info, .video-js .vjs-control.info, .video-js .vjs-info, .video-js .vjs-control .vjs-icon-info, .vjs-icon-info, button[title*="info" i], button[aria-label*="info" i], [data-title*="info" i] { display: none !important; visibility: hidden !important; }
      `;
            document.head.appendChild(style);
        }

        if (!playerRef.current) {
            // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
            const videoElement = document.createElement('video-js');

            videoElement.classList.add('vjs-big-play-centered');
            videoRef.current.appendChild(videoElement);

            const player = (playerRef.current = videojs(videoElement, options, () => {
                videojs.log('player is ready');
                onReady && onReady(player);
            }));
            player.addClass('vjs-theme-city'); // Add the city theme class

            // Ensure player (and its tech) have a valid eventBusEl_ (DOM node)
            // so that EventedMixin.trigger calls (from plugins like videojs-youtube)
            // don't throw "Invalid target" if an async image.onload fires after
            // some internal cleanup. This guards against cases where the
            // internal eventBusEl_ might be missing during plugin callbacks.
            try {
                const el = player && typeof player.el === 'function' ? player.el() : null;
                if (el && !player.eventBusEl_) player.eventBusEl_ = el;
                try {
                    const tech = player && typeof player.tech === 'function' ? player.tech() : null;
                    if (tech && !tech.eventBusEl_) tech.eventBusEl_ = el;
                } catch (err) {
                    // ignore
                    console.error(err);
                }
            } catch (err) {
                // ignore
                console.error(err);
            }

            // If options contains a poster, set it on the player so video.js will show it
            if (options?.poster) {
                try {
                    player.poster(options.poster);
                } catch {
                    // ignore poster set errors
                }
            }
            // Hide the big play button to reduce overlays
            try {
                const bigPlay = player.getChild && player.getChild('BigPlayButton');
                if (bigPlay && bigPlay.hide) bigPlay.hide();
            } catch {
                // ignore
            }

            // Remove/hide info buttons programmatically (covers plugins/themes that inject differently)
            const removeInfoButtons = () => {
                try {
                    const wrap = videoRef.current;
                    if (!wrap) return;
                    const selectors = [
                        '.vjs-info-button',
                        '.vjs-control.vjs-info',
                        '.vjs-control.info',
                        '.vjs-info',
                        '.vjs-control .vjs-icon-info',
                        '.vjs-icon-info',
                        'button[title*="info" i]',
                        'button[aria-label*="info" i]',
                        '[data-title*="info" i]',
                    ];
                    const elems = wrap.querySelectorAll(selectors.join(','));
                    elems.forEach(el => {
                        try {
                            el.style.display = 'none';
                            el.style.visibility = 'hidden';
                            if (el.remove) el.remove();
                        } catch (err) {
                            void err;
                        }
                    });
                } catch (err) {
                    void err;
                }
            };

            // run once now
            removeInfoButtons();

            // observe for mutations (plugins might inject controls later)
            try {
                const wrap = videoRef.current;
                if (wrap && window.MutationObserver) {
                    const obs = new MutationObserver(() => removeInfoButtons());
                    obs.observe(wrap, { childList: true, subtree: true });
                    observerRef.current = obs;
                }
            } catch (err) {
                void err;
            }

            // Add a play overlay element on top of the poster so users know it's playable
            try {
                const wrap = videoRef.current;
                let overlay = wrap.querySelector('.vjs-play-overlay');
                if (!overlay) {
                    overlay = document.createElement('button');
                    overlay.setAttribute('type', 'button');
                    overlay.className = 'vjs-play-overlay';
                    overlay.setAttribute('aria-label', 'Play video');
                    overlay.innerHTML = `
            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
              <path d="M8 5v14l11-7z"></path>
            </svg>
          `;
                    // clicking overlay should start playback
                    overlay.addEventListener('click', () => {
                        try {
                            player.play();
                        } catch (err) {
                            void err;
                        }
                    });
                    // position overlay inside wrapper
                    wrap.style.position = wrap.style.position || 'relative';
                    wrap.appendChild(overlay);
                }

                // initialize overlay visibility based on current player state
                try {
                    if (player && typeof player.paused === 'function') {
                        overlay.style.display = player.paused() ? 'flex' : 'none';
                    }
                } catch (err) {
                    void err;
                }
            } catch (err) {
                void err;
            }

            // helper to detect youtube sources
            const isYouTubeSource = opts => {
                try {
                    const src = (opts?.sources && opts.sources[0] && opts.sources[0].src) || '';
                    return /youtube.com|youtu.be/.test(src);
                } catch {
                    return false;
                }
            };

            // If the source is a YouTube URL, append params that reduce suggestions/endscreen
            if (isYouTubeSource(options)) {
                try {
                    const raw = options.sources[0].src;
                    const parsed = new URL(raw, window.location.href);
                    const params = parsed.searchParams;
                    params.set('rel', '0');
                    params.set('modestbranding', '1');
                    params.set('iv_load_policy', '3');
                    // Hide video title/uploader/info overlays when possible
                    params.set('showinfo', '0');
                    // Ensure controls are visible (prevents some players from rendering extra chrome)
                    params.set('controls', '1');
                    params.set('playsinline', '1');
                    params.set('enablejsapi', '1');
                    try {
                        params.set('origin', window.location.origin);
                    } catch (err) {
                        void err;
                    }
                    parsed.search = params.toString();
                    player.src({ src: parsed.toString(), type: 'video/youtube' });
                } catch {
                    // fallback: leave source as provided
                }
            }

            // Add handlers to hide the iframe on pause/ended and show poster instead (reduces YouTube overlays)
            player.on('pause', () => {
                if (isYouTubeSource(options)) {
                    player.addClass && player.addClass('vjs-hide-iframe-on-pause');
                    if (options?.poster)
                        try {
                            player.poster(options.poster);
                        } catch (err) {
                            void err;
                        }
                }
            });

            player.on('play', () => {
                if (isYouTubeSource(options)) {
                    player.removeClass && player.removeClass('vjs-hide-iframe-on-pause');
                }
                // hide overlay when playback starts
                try {
                    const wrap = videoRef.current;
                    const overlay = wrap && wrap.querySelector('.vjs-play-overlay');
                    if (overlay) overlay.style.display = 'none';
                } catch (err) {
                    void err;
                }
            });

            player.on('ended', () => {
                if (isYouTubeSource(options)) {
                    try {
                        player.pause();
                        if (player.currentTime) player.currentTime(0);
                        player.addClass && player.addClass('vjs-hide-iframe-on-pause');
                        if (options?.poster)
                            try {
                                player.poster(options.poster);
                            } catch (err) {
                                void err;
                            }
                    } catch {
                        // ignore
                    }
                }
                // show overlay when playback ends
                try {
                    const wrap = videoRef.current;
                    const overlay = wrap && wrap.querySelector('.vjs-play-overlay');
                    if (overlay) overlay.style.display = 'flex';
                } catch (err) {
                    void err;
                }
            });

            // also show overlay on pause for all sources
            player.on('pause', () => {
                try {
                    const wrap = videoRef.current;
                    const overlay = wrap && wrap.querySelector('.vjs-play-overlay');
                    if (overlay) overlay.style.display = 'flex';
                } catch (err) {
                    void err;
                }
            });
        } else {
            const player = playerRef.current;

            player.autoplay(!!options?.autoplay);
            player.src(options?.sources || []);

            // Update poster reactively when options change
            if (options?.poster) {
                try {
                    player.poster(options.poster);
                } catch {
                    // ignore
                }
            } else {
                try {
                    player.poster('');
                } catch {
                    // ignore
                }
            }
        }
    }, [options, onReady]);
    useEffect(() => {
        const player = playerRef.current;

        return () => {
            try {
                if (observerRef.current) {
                    observerRef.current.disconnect();
                    observerRef.current = null;
                }
            } catch (err) {
                void err;
            }

            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, []);

    return (
        <div data-vjs-player>
            <div ref={videoRef} className="my-2" />
        </div>
    );
};

export default VideoPlayer;
// Usage example:
// <VideoPlayer
//   options={{
//     controls: true,
//     autoplay: false,
//     preload: "auto",
//     sources: [
//       {
//         src: "https://www.youtube.com/watch?v=your_video_id",
//         type: "video/youtube"
//       },
//     ],
//     youtube: {
//       iv_load_policy: 3, // Disable annotations
//       modestbranding: 1, // Minimal YouTube branding
//       rel: 0, // Disable related videos at the end
//       showinfo: 0, // Hide video title and uploader
//       controls: 1, // Show player controls
//       playsinline: 1, // Play inline on mobile devices
//     },
//   }}
